import { useState, useEffect, useCallback } from 'react'
import { clsx } from 'clsx'
import { FileText, Building2, User, Users, Building, Banknote } from 'lucide-react'

interface SectionNavProps {
  sectionIds: string[]
}

const sectionConfig = [
  { id: 'contract-type', label: 'Tipo de Contrato', icon: FileText },
  { id: 'property', label: 'Inmueble', icon: Building2 },
  { id: 'tenant', label: 'Arrendatario', icon: User },
  { id: 'guarantors', label: 'Deudores Solidarios', icon: Users },
  { id: 'owners', label: 'Propietarios', icon: Building },
  { id: 'terms', label: 'Condiciones', icon: Banknote },
]

export function SectionNav({ sectionIds }: SectionNavProps) {
  const [activeSection, setActiveSection] = useState(sectionIds[0])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        }
      },
      {
        rootMargin: '-20% 0px -60% 0px',
        threshold: 0,
      }
    )

    for (const id of sectionIds) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }

    return () => observer.disconnect()
  }, [sectionIds])

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const visibleSections = sectionConfig.filter((s) => sectionIds.includes(s.id))

  return (
    <>
      {/* Desktop: sticky sidebar */}
      <nav
        aria-label="Secciones del formulario"
        className="hidden lg:block sticky top-24 self-start"
      >
        <ul className="space-y-1">
          {visibleSections.map((section) => {
            const Icon = section.icon
            const isActive = activeSection === section.id
            return (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => scrollTo(section.id)}
                  className={clsx(
                    'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg transition-colors text-left',
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
                  )}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  {section.label}
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* Mobile: sticky horizontal bar */}
      <nav
        aria-label="Secciones del formulario"
        className="lg:hidden sticky top-0 z-10 bg-white border-b border-neutral-200 -mx-4 px-4 overflow-x-auto"
      >
        <ul className="flex gap-1 py-2">
          {visibleSections.map((section) => {
            const isActive = activeSection === section.id
            return (
              <li key={section.id}>
                <button
                  type="button"
                  onClick={() => scrollTo(section.id)}
                  className={clsx(
                    'whitespace-nowrap px-3 py-1.5 text-xs rounded-full transition-colors',
                    isActive
                      ? 'bg-primary-100 text-primary-700 font-medium'
                      : 'text-neutral-500 hover:bg-neutral-100'
                  )}
                >
                  {section.label}
                </button>
              </li>
            )
          })}
        </ul>
      </nav>
    </>
  )
}
